import { useEffect, useMemo, useRef, useState } from 'react';

declare global { interface Window { kakao?: { maps: { load: (callback: () => void) => void; Map: new (element: HTMLElement, options: object) => unknown; LatLng: new (lat: number, lng: number) => unknown; Marker: new (options: object) => unknown } } } }

let mapsLoader: Promise<void> | null = null;

function loadKakaoMaps(key: string): Promise<void> {
  if (window.kakao?.maps?.Map && window.kakao.maps.LatLng) return Promise.resolve();
  if (mapsLoader) return mapsLoader;
  mapsLoader = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(new Error('Kakao Maps loading timed out.')), 12_000);
    const finish = (error?: Error) => {
      window.clearTimeout(timeout);
      if (error) { mapsLoader = null; reject(error); } else resolve();
    };
    const script = document.createElement('script');
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(key)}&autoload=false`;
    script.async = true;
    script.onload = () => {
      if (!window.kakao?.maps?.load) {
        finish(new Error('Kakao Maps is unavailable.'));
        return;
      }
      window.kakao.maps.load(() => {
        if (window.kakao?.maps?.Map && window.kakao.maps.LatLng) finish();
        else finish(new Error('Kakao Maps is unavailable.'));
      });
    };
    script.onerror = () => finish(new Error('Kakao Maps could not be loaded.'));
    document.head.appendChild(script);
  });
  return mapsLoader;
}

function kakaoMapUrl(name: string, address: string, latitude: number | null, longitude: number | null) {
  if (latitude != null && longitude != null) return `https://map.kakao.com/link/map/${encodeURIComponent(name)},${latitude},${longitude}`;
  return `https://map.kakao.com/link/search/${encodeURIComponent(address || name)}`;
}

function openStreetMapEmbedUrl(latitude: number, longitude: number) {
  const delta = 0.004;
  const bbox = [longitude - delta, latitude - delta, longitude + delta, latitude + delta].join('%2C');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude}%2C${longitude}`;
}

export function VenueMap({ name, address, latitude, longitude }: { name: string; address: string; latitude: number | null; longitude: number | null; geocodeStatus?: 'pending' | 'complete' | 'failed' | 'not_available' }) {
  const container = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>(() => latitude != null && longitude != null ? 'loading' : 'ready');
  const key = import.meta.env.VITE_KAKAO_MAP_JAVASCRIPT_KEY?.trim();
  const mapUrl = useMemo(() => kakaoMapUrl(name, address, latitude, longitude), [name, address, latitude, longitude]);
  const hasCoordinates = latitude != null && longitude != null;

  useEffect(() => {
    if (!key || latitude == null || longitude == null || !container.current) return;
    let active = true;
    setState('loading');
    const render = () => {
      if (!window.kakao?.maps || !container.current) throw new Error('Kakao Maps is unavailable.');
      const position = new window.kakao.maps.LatLng(latitude, longitude);
      const map = new window.kakao.maps.Map(container.current, { center: position, level: 3 });
      new window.kakao.maps.Marker({ position, map, title: name });
      if (active) setState('ready');
    };
    loadKakaoMaps(key).then(render).catch(() => { if (active) setState('failed'); });
    return () => { active = false; };
  }, [key, latitude, longitude, name]);

  if (!hasCoordinates) return <section className="venue-location-card">
    <span className="venue-location-mark" aria-hidden="true">⌖</span>
    <div><p className="eyebrow">Location</p><strong>{address || '등록된 상세 주소가 없습니다.'}</strong><small>{address ? '외부 지도에서 장소명과 주소를 함께 확인할 수 있습니다.' : '공연 기록에 위치 정보가 추가되면 이곳에 표시됩니다.'}</small></div>
    <a className="button button-secondary" href={mapUrl} target="_blank" rel="noreferrer">카카오맵에서 검색 ↗</a>
  </section>;

  const fallback = !key ? ['위치는 등록되어 있습니다.', '카카오맵에서 정확한 위치를 확인할 수 있습니다.']
    : state === 'failed' ? ['지도를 불러오지 못했습니다.', '카카오맵에서 장소를 열어 정확한 위치를 확인해보세요.']
      : ['지도 준비 중…', address || name];
  const showFallback = !key || state !== 'ready';
  return <section className="venue-map-card">
    <div className="venue-map" ref={container} aria-label={`${name} 지도`}>
      {showFallback && <div className={`map-fallback ${state === 'loading' ? 'is-loading' : ''}`}><span aria-hidden="true">⌖</span><strong>{fallback[0]}</strong><small>{fallback[1]}</small></div>}
    </div>
    <a className="button button-secondary button-full" href={mapUrl} target="_blank" rel="noreferrer">카카오맵에서 위치 확인 ↗</a>
  </section>;
}

export function VenueMapPreview({ name, address, latitude, longitude, placeUrl }: { name: string; address: string; latitude: number; longitude: number; placeUrl?: string }) {
  const container = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const key = import.meta.env.VITE_KAKAO_MAP_JAVASCRIPT_KEY?.trim();
  const mapUrl = placeUrl || kakaoMapUrl(name, address, latitude, longitude);
  const fallbackMapUrl = openStreetMapEmbedUrl(latitude, longitude);

  useEffect(() => {
    if (!key || !container.current) { setState('failed'); return; }
    let active = true;
    const render = () => {
      if (!window.kakao?.maps || !container.current) throw new Error('Kakao Maps is unavailable.');
      const position = new window.kakao.maps.LatLng(latitude, longitude);
      const map = new window.kakao.maps.Map(container.current, { center: position, level: 3 });
      new window.kakao.maps.Marker({ position, map, title: name });
      if (active) setState('ready');
    };
    setState('loading');
    loadKakaoMaps(key).then(render).catch(() => { if (active) setState('failed'); });
    return () => { active = false; };
  }, [key, latitude, longitude, name]);

  return <div className="venue-search-preview">
    <div className="venue-search-map" ref={container} aria-label={`${name} 지도 미리보기`}>
      {state !== 'ready' && <iframe title={`${name} 위치 미리보기`} src={fallbackMapUrl} loading="lazy" referrerPolicy="no-referrer" />}
    </div>
    <a href={mapUrl} target="_blank" rel="noreferrer">카카오맵에서 크게 보기 ↗</a>
  </div>;
}
